import {
  HourlyTuitionBatchOutcome,
  HourlyTuitionPostStudentStatus,
} from './hourly-tuition.constants';
import type {
  PostTuitionRequest,
  PostTuitionResponse,
  StudentResult,
} from './post-to-accounts'; // type-only: no runtime cycle with postTuitionInChunks

/** Keep each HTTP post under App Engine / gateway request deadlines. */
export const HOURLY_TUITION_POST_CHUNK_SIZE = 25;

export const chunkStudents = <T>(
  students: Array<T>,
  chunkSize: number = HOURLY_TUITION_POST_CHUNK_SIZE,
): Array<Array<T>> => {
  if (chunkSize <= 0) {
    throw new Error('chunkSize must be > 0');
  }
  if (students.length === 0) return [];
  const chunks: Array<Array<T>> = [];
  for (let i = 0; i < students.length; i += chunkSize) {
    chunks.push(students.slice(i, i + chunkSize));
  }
  return chunks;
};

export const resolveChunkedBatchOutcome = (
  results: Array<StudentResult>,
): HourlyTuitionBatchOutcome => {
  if (results.length === 0) return HourlyTuitionBatchOutcome.FAILED;

  const successCount = results.filter(
    (row) => row.status === HourlyTuitionPostStudentStatus.SUCCESS,
  ).length;
  const failedCount = results.filter(
    (row) => row.status === HourlyTuitionPostStudentStatus.FAILED,
  ).length;

  if (successCount > 0 && failedCount === 0) {
    return HourlyTuitionBatchOutcome.SUCCESS;
  }
  if (successCount > 0 && failedCount > 0) {
    return HourlyTuitionBatchOutcome.PARTIAL;
  }
  if (successCount === 0 && failedCount > 0) {
    return HourlyTuitionBatchOutcome.FAILED;
  }
  // Only skips / already-processed rows
  return HourlyTuitionBatchOutcome.SUCCESS;
};

export const mergeChunkedPostResponses = (
  chunks: Array<PostTuitionResponse>,
): PostTuitionResponse => {
  const results = chunks.flatMap((chunk) => chunk.results ?? []);
  const processedStudents = results.filter(
    (row) => row.status === HourlyTuitionPostStudentStatus.SUCCESS,
  ).length;
  const outcome = resolveChunkedBatchOutcome(results);
  const firstMessage = chunks.find((chunk) => chunk.message)?.message;

  return {
    success: processedStudents > 0,
    outcome,
    processedStudents,
    results,
    message: firstMessage,
  };
};

export const buildChunkRequest = (
  data: PostTuitionRequest,
  students: PostTuitionRequest['students'],
): PostTuitionRequest => ({
  ...data,
  students,
});
